import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserLoginModel } from '../model/userLogin.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { CustomHttpResponse } from '../model/custom-http-response';
import { environment } from '../../environments/environment';
import { JwtPayload } from './jwt.service';
import { CreateUserDtoModel } from '../model/createUserDto.model';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  host = environment.apiUrl;
  private token: string | null | undefined = null;
  private jwtHelper: JwtHelperService;
  private loggedInUsername: string | null = null;
  private role!: string;
  public currentUserSubject: BehaviorSubject<JwtPayload | null> =
    new BehaviorSubject<JwtPayload | null>(null);

  public currentUser$: Observable<JwtPayload | null> = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    jwtHelper: JwtHelperService
  ) {
    this.jwtHelper = jwtHelper;
    this.loadCurrentUser();
  }

  public isAdmin(): boolean {
    this.loadToken();
    if (this.token) {
      const decodedToken = this.jwtHelper.decodeToken(this.token);
      return decodedToken?.role === 'ROLE_SUPER_ADMIN';
    }
    return false;
  }

  public getUserFromLocalStorage(): JwtPayload | null {
    const user = localStorage.getItem('userPayload');
    return user ? JSON.parse(user) : null;
  }
  private loadCurrentUser(): void {
    const user = this.getUserFromLocalStorage();
    this.currentUserSubject.next(user);
  }
  public login(user: UserLoginModel): Observable<HttpResponse<CustomHttpResponse>> {
    const url = `${this.host}/auth/login`;
    return this.http.post<CustomHttpResponse>(url, user, { observe: 'response' });
  }

  public saveToken(token: string | null | undefined): void {
    this.token = token;
    if (typeof token === 'string') {
      localStorage.setItem('token', token);
    }
  }

  public Logout(logFromAll: boolean): Observable<HttpResponse<CustomHttpResponse>> {
    const url = `${this.host}/auth/logout`;
    const params = new HttpParams().set('logFromAll', logFromAll);
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.getToken()}`);
    this.token = null;
    return this.http.post<CustomHttpResponse>(url, null, { observe: 'response', params, headers });
  }

  public getToken(): string | null {
    return localStorage.getItem('token');
  }

  public loadToken(): void {
    this.token = localStorage.getItem('token');
  }

  private isTokenValid(): boolean {
    if (this.token != null && this.token != '') {
      const { sub } = this.jwtHelper.decodeToken(this.token);
      if (sub != null && sub !== '' && !this.jwtHelper.isTokenExpired(this.token)) {
        this.loggedInUsername = sub;
        return true;
      }
    }
    return false;
  }

  public isLogin(): boolean {
    this.loadToken();
    if (this.isTokenValid()) {
      return true;
    } else {
      this.token = null;
      localStorage.removeItem('token');
      return false;
    }
  }
  register(user: CreateUserDtoModel): Observable<HttpResponse<CustomHttpResponse>> {
    const url = `${this.host}/auth/register`;
    return this.http.post<CustomHttpResponse>(url, user, { observe: 'response' });
  }
  resetPassword(email: string): Observable<HttpResponse<CustomHttpResponse>> {
    const body = { email };
    return this.http.post<CustomHttpResponse>(`${this.host}/auth/password-reset/request`, body, {
      observe: 'response',
    });
  }
  confirmPassword(
    newPassword: string,
    retypePassword: string,
    key: string | undefined,
    token: string | undefined
  ): Observable<HttpResponse<CustomHttpResponse>> {
    const body = { newPassword, retypePassword, key, token };
    return this.http.patch<CustomHttpResponse>(`${this.host}/auth/password-reset/update`, body, {
      observe: 'response',
    });
  }
}
